import { UserCircleIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import StatCell from '../../../components/analysis/StatCell';
import { getNormalizedSegments, getSegmentsArray } from '../../../utils/comparativeSegmentsHelper';

export default function ComparativeTestBreakdownTable({
  selectedSubject,
  comparativeTestBreakdown,
  userTestBreakdown,
  totalQuestions,
  comparativeSegmentsMeta,
}) {
  const [subjectComparativeBreakdown, setSubjectComparativeBreakdown] = useState(null);
  const [subjectUserBreakdown, setSubjectUserBreakdown] = useState(null);
  const [segments, setSegments] = useState({});

  useEffect(() => {
    const normalizedSegments = getNormalizedSegments(comparativeSegmentsMeta);
    setSegments(normalizedSegments);
  }, [comparativeSegmentsMeta]);

  useEffect(() => {
    if (!selectedSubject) return;

    const comparative = comparativeTestBreakdown?.filter(
      (item) => item?.subject_id?.toString() === selectedSubject?.id?.toString(),
    );
    const user = userTestBreakdown?.filter((item) => item?.subject_id?.toString() === selectedSubject?.id?.toString());
    setSubjectComparativeBreakdown(comparative);
    setSubjectUserBreakdown(user);
  }, [selectedSubject]);

  if (!selectedSubject || !subjectComparativeBreakdown || !subjectUserBreakdown || !totalQuestions) return null;

  const totalQ =
    selectedSubject?.id?.toString() === 'overall'
      ? totalQuestions.total
      : totalQuestions[selectedSubject?.id?.toString()];

  const getComparativeValue = (type, key) => {
    return subjectComparativeBreakdown?.[0]?.values?.find((item) => item.title === type)?.values?.[key];
  };

  const renderStatCell = (color, top, bottom) => {
    return top !== null && top !== undefined ? (
      <StatCell color={color} top={top} bottom={bottom} />
    ) : (
      <div className="flex items-center justify-center h-8 text-xs text-[var(--q3-neutral-default)]">No data</div>
    );
  };

  // Handle case differences in API keys (e.g. "notVisited" vs "notvisited") to keep old and new responses compatible
  function getValueCaseInsensitive(obj, key) {
    const foundKey = Object.keys(obj || {}).find((k) => k.toLowerCase() === key.toLowerCase());
    return obj?.[foundKey];
  }

  const renderUserStatCell = (color, key) => {
    const top = getValueCaseInsensitive(subjectUserBreakdown?.[0], key);
    return renderStatCell(color, top, totalQ);
  };

  const renderRow = (icon, label, dataSource, colorMap) => (
    <tr className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex flex-row justify-start items-center px-4 gap-4">
          {icon}
          <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">{label}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">{dataSource('Correct', colorMap.correct)}</td>
      <td className="border-r border-[var(--q3-stroke-light)]">{dataSource('Incorrect', colorMap.incorrect)}</td>
      <td className="border-r border-[var(--q3-stroke-light)]">{dataSource('Unattempted', colorMap.unattempted)}</td>
      <td>{dataSource('Not Visited', colorMap.notVisited)}</td>
    </tr>
  );

  const renderComparativeRow = (segment) => (
    <tr key={segment.key} className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex flex-row justify-start items-center px-4 gap-4">
          <segment.icon size={24} className="text-[var(--q3-neutral-default)]" />
          <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">{segment.title}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">
        {renderStatCell('var(--q3-green-normal)', getComparativeValue('Correct', segment.key), totalQ)}
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">
        {renderStatCell('var(--q3-danger-normal)', getComparativeValue('Incorrect', segment.key), totalQ)}
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">
        {renderStatCell('var(--q3-accent-normal)', getComparativeValue('Unattempted', segment.key), totalQ)}
      </td>
      <td>{renderStatCell('var(--q3-neutral-light)', getComparativeValue('Not Visited', segment.key), totalQ)}</td>
    </tr>
  );

  const comparativeSegmentsArray = getSegmentsArray(segments, 'topper');

  return (
    <div className="overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
      <table className="text-sm w-full">
        <thead className="bg-[var(--q3-surface-dim)]">
          <tr className="h-16">
            <td className="py-3 px-4 w-[318px] text-start border-r border-[var(--q3-stroke-light)]">
              <div className="line-clamp-2 max-w-[300px] leading-snug">Students</div>
            </td>
            {['Attempted Correct', 'Attempted Wrong', 'Not Attempted', 'Not Visited Qs'].map((text, idx, arr) => (
              <td
                key={idx}
                className={`py-3 px-4 min-w-[114px] text-center align-middle ${idx < arr.length - 1 && 'border-r border-[var(--q3-stroke-light)]'}`}
              >
                <div
                  className={`line-clamp-2 max-w-[114px] mx-auto leading-snug ${
                    idx === 0
                      ? 'text-[var(--q3-green-normal)]'
                      : idx === 1
                        ? 'text-[var(--q3-danger-normal)]'
                        : idx === 2
                          ? 'text-[var(--q3-accent-normal)]'
                          : 'text-[var(--q3-neutral-light)]'
                  }`}
                >
                  {text}
                </div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderRow(
            <UserCircleIcon size={24} className="text-[var(--q3-neutral-default)]" />,
            'You',
            (type, color) => renderUserStatCell(color, type.toLowerCase().replace(' ', '')),
            {
              correct: 'var(--q3-green-normal)',
              incorrect: 'var(--q3-danger-normal)',
              unattempted: 'var(--q3-accent-normal)',
              notVisited: 'var(--q3-neutral-light)',
            },
          )}

          {comparativeSegmentsArray.map((segment) => renderComparativeRow(segment))}
        </tbody>
      </table>
    </div>
  );
}
